<template>
  <div class="settings-panel">
    <p v-if="loading" role="status">กำลังโหลดค่าตั้งค่า…</p>
    <div v-else-if="loadError" role="alert" class="text-danger">
      {{ loadError }} <vs-button type="border" @click="load">ลองใหม่</vs-button>
    </div>
    <template v-else>
      <vx-card v-for="card in settingCards" :key="card.id" class="mb-6">
        <form @submit.prevent="saveCard(card)">
          <section
            v-for="group in card.groups"
            :key="group.id"
            class="settings-section"
            :aria-labelledby="`${group.id}-heading`"
          >
            <div class="settings-heading">
              <div>
                <h4 :id="`${group.id}-heading`">{{ group.title }}</h4>
                <p>{{ group.description }}</p>
              </div>
              <span v-if="dirty(group)" class="text-warning"
                >ยังไม่ได้บันทึก</span
              >
            </div>
            <div class="settings-grid">
              <div
                v-for="field in group.fields"
                :key="field.key"
                :class="{
                  'settings-wide': ['emails', 'switch'].includes(
                    field.inputType
                  ),
                }"
              >
                <label v-if="field.inputType !== 'switch'" :for="field.key">{{
                  field.label
                }}</label>
                <div
                  v-if="field.inputType === 'switch'"
                  class="settings-switch"
                >
                  <vs-switch
                    type="button"
                    :aria-label="field.label"
                    :value="draft[field.key] === 'true'"
                    :disabled="busy[card.id]"
                    @input="$set(draft, field.key, String($event))"
                  />
                  <span>{{ field.label }}</span>
                </div>
                <textarea
                  v-else-if="field.inputType === 'emails'"
                  :id="field.key"
                  v-model="draft[field.key]"
                  :disabled="busy[card.id]"
                  rows="3"
                  maxlength="2000"
                />
                <input
                  v-else-if="field.inputType === 'time'"
                  :id="field.key"
                  type="time"
                  :value="asTime(draft[field.key])"
                  :disabled="busy[card.id]"
                  required
                  @input="
                    $set(
                      draft,
                      field.key,
                      $event.target.value.replace(':', '.')
                    )
                  "
                />
                <input
                  v-else
                  :id="field.key"
                  v-model="draft[field.key]"
                  :type="field.inputType"
                  :disabled="busy[card.id]"
                  required
                  maxlength="2000"
                />
                <p v-if="field.description" class="settings-help">
                  {{ field.description }}
                </p>
              </div>
            </div>
          </section>
          <div class="settings-footer">
            <p v-if="messages[card.id]" role="status" aria-live="polite">
              {{ messages[card.id] }}
            </p>
            <div class="settings-buttons">
              <vs-button
                type="border"
                :disabled="busy[card.id] || !cardDirty(card)"
                @click="resetCard(card)"
                >คืนค่า</vs-button
              >
              <button
                type="submit"
                class="settings-save"
                :disabled="busy[card.id] || !cardDirty(card)"
              >
                {{ busy[card.id] ? "กำลังบันทึก…" : "บันทึก" }}
              </button>
            </div>
          </div>
        </form>
      </vx-card>
    </template>
  </div>
</template>

<script>
import gql from "graphql-tag";
import { apolloClient } from "@/setup/apollo-setup";

const fields = gql`
  fragment SettingsGroupFields on SystemSettingGroup {
    id
    title
    description
    warning
    fields {
      key
      label
      description
      inputType
      valueType
      value
    }
  }
`;
const read = gql`
  query EditableSystemSettings {
    systemSettings {
      ...SettingsGroupFields
    }
  }
  ${fields}
`;
const update = gql`
  mutation SaveSystemSettingGroup(
    $groupId: String!
    $values: [SystemSettingValueInput!]!
  ) {
    updateSystemSettings(groupId: $groupId, values: $values) {
      ...SettingsGroupFields
    }
  }
  ${fields}
`;

export default {
  props: {
    groupIds: { type: Array, default: () => [] },
  },
  computed: {
    visibleGroups() {
      return this.groups.filter((group) => this.groupIds.includes(group.id));
    },
    settingCards() {
      const cards = [];
      for (const group of this.visibleGroups) {
        const id = ["system", "qr", "payment"].includes(group.id)
          ? "availability"
          : group.id;
        const card = cards.find((item) => item.id === id);
        if (card) card.groups.push(group);
        else cards.push({ id, groups: [group] });
      }
      return cards;
    },
  },
  data() {
    return {
      loading: true,
      loadError: "",
      groups: [],
      draft: {},
      busy: {},
      messages: {},
      disposed: false,
    };
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.disposed = true;
  },
  methods: {
    cardDirty(card) {
      return card.groups.some((group) => this.dirty(group));
    },
    resetCard(card) {
      card.groups.forEach((group) => this.reset(group));
      this.$set(this.messages, card.id, "");
    },
    saveCard(card) {
      return this.save({
        id: card.id,
        fields: card.groups.flatMap((group) => group.fields),
      });
    },
    asTime(value) {
      return String(value).replace(".", ":").padStart(5, "0");
    },
    dirty(group) {
      return group.fields.some(
        (field) => this.draft[field.key] !== field.value
      );
    },
    reset(group) {
      group.fields.forEach((field) =>
        this.$set(this.draft, field.key, field.value)
      );
      this.$set(this.messages, group.id, "");
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      try {
        const result = await apolloClient.query({
          query: read,
          fetchPolicy: "no-cache",
        });
        if (this.disposed) return;
        this.groups = result.data.systemSettings;
        this.groups.forEach((group) => this.reset(group));
      } catch (error) {
        if (!this.disposed)
          this.loadError =
            "โหลดค่าตั้งค่าไม่สำเร็จ กรุณาตรวจสอบสิทธิ์ การเชื่อมต่อ และการย้ายข้อมูล SystemSetting ก่อนใช้งาน";
      } finally {
        if (!this.disposed) this.loading = false;
      }
    },
    async save(group) {
      if (this.busy[group.id] || !this.dirty(group)) return;
      if (group.id === "availability") {
        const ranges = ["system", "qr", "payment"].map((id) => ({
          start: this.draft[`${id}.open_time`],
          end: this.draft[`${id}.close_time`],
        }));
        const valid = ranges.every(
          ({ start, end }) =>
            /^([01]?\d|2[0-3])\.[0-5]\d$/.test(start) &&
            /^([01]?\d|2[0-3])\.[0-5]\d$/.test(end) &&
            Number(start) < Number(end)
        );
        if (
          !valid ||
          ranges
            .slice(1)
            .some(
              ({ start, end }) =>
                Number(start) < Number(ranges[0].start) ||
                Number(end) > Number(ranges[0].end)
            )
        ) {
          this.$set(
            this.messages,
            group.id,
            "ช่วงเปิดออก QR Code และรับชำระเงินต้องอยู่ภายในช่วงเปิดระบบ และเวลาเปิดต้องน้อยกว่าเวลาปิด"
          );
          return;
        }
      }
      this.$set(this.busy, group.id, true);
      this.$set(this.messages, group.id, "");
      try {
        const result = await apolloClient.mutate({
          mutation: update,
          variables: {
            groupId: group.id,
            values: group.fields.map((field) => ({
              key: field.key,
              value: this.draft[field.key],
              expectedValue: field.value,
            })),
          },
        });
        if (this.disposed) return;
        const saved = result.data.updateSystemSettings;
        this.groups.forEach((item, index) => {
          if (
            !item.fields.some((field) =>
              saved.fields.some((updated) => updated.key === field.key)
            )
          )
            return;
          const updated = {
            ...item,
            fields: item.fields.map(
              (field) =>
                saved.fields.find((value) => value.key === field.key) || field
            ),
          };
          this.$set(this.groups, index, updated);
          this.reset(updated);
        });
        this.$set(
          this.messages,
          group.id,
          saved.warning || "บันทึกเรียบร้อยแล้ว"
        );
      } catch (error) {
        if (!this.disposed) {
          const safeError = (error.graphQLErrors || []).find(
            (item) =>
              item.extensions &&
              [
                "system_settings_invalid",
                "system_settings_conflict",
                "system_settings_not_ready",
              ].includes(item.extensions.code)
          );
          this.$set(
            this.messages,
            group.id,
            safeError
              ? safeError.message
              : "ไม่สามารถยืนยันผลบันทึกได้ กรุณาโหลดหน้าใหม่เพื่อตรวจสอบค่าก่อนลองอีกครั้ง"
          );
        }
      } finally {
        if (!this.disposed) this.$set(this.busy, group.id, false);
      }
    },
  },
};
</script>

<style scoped>
.settings-section + .settings-section {
  border-top: 1px solid #d8d6de;
  margin-top: 28px;
  padding-top: 28px;
}
.settings-panel {
  width: 100%;
}
.settings-heading p,
.settings-help {
  color: #6e6b7b;
  margin-top: 8px;
}
.settings-heading {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}
.settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}
.settings-wide {
  grid-column: 1 / -1;
}
label {
  display: block;
  margin-bottom: 8px;
}
input:not([type="checkbox"]),
textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #d8d6de;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font: inherit;
}
input:focus,
textarea:focus {
  outline: 2px solid #7367f0;
  outline-offset: 1px;
}
.settings-switch {
  display: flex;
  align-items: center;
  gap: 12px;
}
.settings-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  border-top: 1px solid #eee;
  margin-top: 24px;
  padding-top: 20px;
}
.settings-buttons {
  display: flex;
  gap: 12px;
  margin-left: auto;
}
.settings-save {
  background: #7367f0;
  color: white;
  border: 0;
  border-radius: 6px;
  padding: 12px 20px;
  cursor: pointer;
  font: inherit;
}
.settings-save:disabled {
  opacity: 0.5;
  cursor: default;
}
@media (max-width: 640px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }
  .settings-heading {
    flex-direction: column;
  }
}
</style>
